import { useEffect, useState } from "react";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Stat, Card, CardHeader } from "@/components/shared";
import PortfolioValueChart from "@/components/PortfolioValueChart";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, Robot, Circle, Trophy, SpeakerHigh, SpeakerX } from "@phosphor-icons/react";
import { toast } from "sonner";
import OnboardingTour from "@/components/OnboardingTour";
import BadgeGrid from "@/components/BadgeGrid";
import RankTrendChart from "@/components/RankTrendChart";
import RealizedPLSparkline from "@/components/RealizedPLSparkline";
import AlertFeedCard from "@/components/AlertFeedCard";
import BiasHeatmap from "@/components/BiasHeatmap";
import MoodFeedStrip from "@/components/MoodFeedStrip";
import SymbolSentimentChip from "@/components/SymbolSentimentChip";
import { API } from "@/lib/api";
import { playChime, isChimeMuted, setChimeMuted } from "@/lib/chime";
import { fireBadgeConfetti } from "@/lib/confetti";

const INDICES = ["SPY", "QQQ", "DIA"];

export default function DashboardPage() {
  const [portfolio, setPortfolio] = useState(null);
  const [indices, setIndices] = useState([]);
  const [botLogs, setBotLogs] = useState([]);
  const [bots, setBots] = useState([]);
  const [badges, setBadges] = useState([]);
  const [muted, setMuted] = useState(isChimeMuted());
  // Iter 80 — Symbol Sentiment Chip. Batch-fetch latest sentiment
  // for the user's holdings so the dashboard table can render one
  // chip per row without N+1 API calls.
  const [sentimentMap, setSentimentMap] = useState({});

  useEffect(() => {
    load();
    loadBadges();
    const t = setInterval(load, 30000);
    // Poll badges too so a new one earned mid-session fires the reveal toast
    const b = setInterval(loadBadges, 60000);
    return () => { clearInterval(t); clearInterval(b); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const BADGES_SEEN_KEY = "stratos.badges.seen";
  function loadBadges() {
    api.get("/me/badges").then((r) => {
      const next = r.data.badges || [];
      setBadges((prev) => {
        // Fire a celebratory toast when a badge transitions from locked → earned.
        // Uses localStorage so the toast doesn't re-fire on every page load.
        try {
          const stored = JSON.parse(localStorage.getItem(BADGES_SEEN_KEY) || "[]");
          const seenSet = new Set(stored);
          const earnedNow = next.filter((b) => b.earned);
          const freshlyEarned = earnedNow.filter((b) => !seenSet.has(b.id));
          if (freshlyEarned.length > 0 && prev.length > 0) {
            // Only celebrate on subsequent loads — not on the first render for
            // a new session (prev.length=0). The seen-set is still updated so
            // returning users don't see their historical badges pop.
            freshlyEarned.forEach((b) => {
              playChime();
              fireBadgeConfetti();
              toast.success(`${b.emoji} Badge unlocked — ${b.label}`, {
                description: b.description,
                duration: 6000,
              });
            });
          }
          localStorage.setItem(
            BADGES_SEEN_KEY,
            JSON.stringify(earnedNow.map((b) => b.id)),
          );
        } catch {}
        return next;
      });
    }).catch(() => {});
  }

  // Real-time SSE feed: pings when a followed trader deploys a bot.
  useEffect(() => {
    let es;
    try {
      es = new EventSource(`${API}/events/following`, { withCredentials: true });
      es.addEventListener("following_new_bot", (ev) => {
        try {
          const data = JSON.parse(ev.data);
          playChime();
          toast.success(
            `${data.owner_tag} just deployed a new ${data.symbol} ${data.strategy} bot`,
            {
              action: { label: "View", onClick: () => window.location.assign(`/b/${data.bot_id}`) },
            }
          );
        } catch {}
      });
      es.addEventListener("bot_auto_paused", (ev) => {
        try {
          const data = JSON.parse(ev.data);
          playChime();
          toast.warning(data.message || `${data.symbol} bot auto-paused for 24h`, {
            description: data.paused_until
              ? `Resumes ${new Date(data.paused_until).toLocaleString()}`
              : undefined,
            duration: 12000,
            action: {
              label: "Resume anyway",
              onClick: async () => {
                try {
                  await api.post(`/bots/${data.bot_id}/resume`);
                  toast.success(`${data.symbol} bot resumed`);
                } catch (e) {
                  toast.error("Resume failed");
                }
              },
            },
          });
        } catch {}
      });
      // Iter 81 — Sentiment Alerts Bell. When a held symbol's tone
      // flips (bull↔bear) the backend fires a `sentiment_flip`
      // event. We play the chime and show a directional toast so
      // users don't have to keep checking the dashboard chip.
      es.addEventListener("sentiment_flip", (ev) => {
        try {
          const data = JSON.parse(ev.data);
          playChime();
          const up = data.to === "bull";
          const notify = up ? toast.success : toast.warning;
          notify(data.message || `${data.symbol} flipped ${data.from} → ${data.to}`, {
            description: `Sentiment on ${data.symbol} just crossed the ${up ? "bullish" : "bearish"} line`,
            duration: 10000,
          });
          // Re-fetch sentiment map so the on-page chip updates without a full reload.
          try {
            const syms = (portfolio?.holdings || []).map((h) => h.symbol).filter(Boolean);
            if (syms.length) {
              api.get(`/market/sentiment/latest?symbols=${syms.join(",")}`)
                .then((r) => setSentimentMap(r.data?.items || {}))
                .catch(() => {});
            }
          } catch {}
        } catch {}
      });
    } catch {}
    return () => { try { es?.close(); } catch {} };
  }, []);

  async function load() {
    try {
      const [p, l, b] = await Promise.all([
        api.get("/portfolio"),
        api.get("/bots/logs?limit=6"),
        api.get("/bots"),
      ]);
      setPortfolio(p.data);
      setBotLogs(l.data.logs || []);
      setBots(b.data.bots || []);
      const quotes = await Promise.all(INDICES.map(s => api.get(`/market/quote/${s}`).then(r => r.data).catch(() => null)));
      setIndices(quotes.filter(Boolean));
      // Iter 80 — Symbol Sentiment Chip. Batch-fetch latest sentiment
      // for the user's holdings so each row can render a bull/bear
      // chip without firing one request per row.
      const syms = (p.data?.holdings || []).map((h) => h.symbol).filter(Boolean);
      if (syms.length > 0) {
        api.get(`/market/sentiment/latest?symbols=${syms.join(",")}`)
          .then((r) => setSentimentMap(r.data?.items || {}))
          .catch(() => {});
      }
    } catch {}
  }

  const totalValue = portfolio?.total_value || 0;
  const totalPL = portfolio?.total_unrealized_pl || 0;
  const realizedPL = portfolio?.realized_pl || 0;
  const investedValue = portfolio?.invested_value || 0;
  const cash = portfolio?.cash || 0;
  const startingCash = portfolio?.starting_cash || 100000;
  const totalReturn = portfolio?.total_return ?? (totalValue - startingCash);
  const totalReturnPct = portfolio?.total_return_pct ?? ((totalReturn / startingCash) * 100);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
      <OnboardingTour />
      {badges.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Trophy size={14} className="text-[#00C805]" />
              <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
                Badges · {badges.filter((b) => b.earned).length} / {badges.length}
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                const next = !muted;
                setChimeMuted(next);
                setMuted(next);
                if (!next) playChime();
                // Persist server-side so the mute survives device switches.
                // Errors are non-fatal — localStorage is still updated above.
                try { await api.patch("/me/chime-muted", { muted: next }); } catch {}
                toast.info(next ? "Real-time chime muted" : "Chime enabled — you'll hear a subtle blip when a followed trader deploys");
              }}
              data-testid="chime-toggle-btn"
              className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white border border-zinc-800 hover:border-zinc-500 rounded-sm px-2.5 py-1.5 flex items-center gap-1.5"
              title={muted ? "Unmute real-time chime" : "Mute real-time chime"}
            >
              {muted ? <SpeakerX size={11} /> : <SpeakerHigh size={11} />}
              {muted ? "Chime off" : "Chime on"}
            </button>
          </div>
          <BadgeGrid badges={badges} dataTestId="my-badges" />
        </div>
      )}
      <div className="flex items-end justify-between">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Terminal / Overview</div>
          <h1 className="heading text-4xl font-black tracking-tighter mt-2">Dashboard</h1>
        </div>
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
          {new Date().toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat
          label="Total portfolio"
          value={fmtMoney(totalValue)}
          sub={`Return ${fmtPct(totalReturnPct)}`}
          tone={totalReturn >= 0 ? "profit" : "loss"}
        />
        <Stat label="Cash available" value={fmtMoney(cash)} sub="Buying power" />
        <Stat label="Invested" value={fmtMoney(investedValue)} sub={`${portfolio?.holdings?.length || 0} positions`} />
        <Stat
          label="Unrealized P/L"
          value={fmtMoney(totalPL, { showSign: true })}
          sub="Open positions"
          tone={totalPL >= 0 ? "profit" : "loss"}
        />
        <Stat
          label="Realized P/L"
          value={fmtMoney(realizedPL, { showSign: true })}
          sub="Closed trades"
          tone={realizedPL >= 0 ? "profit" : "loss"}
        />
      </div>

      {/* Portfolio value chart + indices + rank sparkline */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-8 min-h-[300px]" data-testid="portfolio-value-chart">
          <PortfolioValueChart startingCash={startingCash} />
        </Card>

        <div className="lg:col-span-4 space-y-4">
          <MoodFeedStrip limit={8} />
          <RankTrendChart days={14} />
          <RealizedPLSparkline weeks={12} />
          <BiasHeatmap />
          <AlertFeedCard limit={5} />
          <Card>
            <CardHeader title="Market indices" />
            <div className="divide-y divide-zinc-800">
              {indices.map((q) => {
                const up = q.change >= 0;
                return (
                  <div key={q.symbol} className="flex items-center justify-between px-5 py-3">
                    <div>
                      <div className="mono text-sm font-semibold">{q.symbol}</div>
                      <div className="text-xs text-zinc-500 truncate max-w-[140px]">{q.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="mono text-sm">{fmtMoney(q.price)}</div>
                      <div className={`mono text-xs flex items-center gap-1 justify-end ${up ? "profit" : "loss"}`}>
                        {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        {fmtPct(q.change_pct)}
                      </div>
                    </div>
                  </div>
                );
              })}
              {indices.length === 0 && <div className="px-5 py-6 mono text-xs text-zinc-600">Loading…</div>}
            </div>
          </Card>
        </div>
      </div>

      {/* Holdings + bot activity */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-7">
          <CardHeader title="Open positions" action={<Link to="/positions" className="mono text-[10px] text-zinc-400 hover:text-white uppercase tracking-widest">View all →</Link>} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
                <tr className="border-b border-zinc-800">
                  <th className="text-left px-5 py-2">Symbol</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Avg cost</th>
                  <th className="text-right px-3 py-2">Price</th>
                  <th className="text-right px-5 py-2">P/L</th>
                </tr>
              </thead>
              <tbody className="mono">
                {(portfolio?.holdings || []).slice(0, 5).map((h) => (
                  <tr key={h.symbol} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`position-row-${h.symbol}`}>
                    <td className="px-5 py-3">
                      <div className="font-semibold flex items-center gap-2">
                        {h.symbol}
                        <SymbolSentimentChip symbol={h.symbol} sentiment={sentimentMap[h.symbol]} />
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[160px]">{h.name}</div>
                    </td>
                    <td className="text-right px-3 py-3">{h.quantity}</td>
                    <td className="text-right px-3 py-3">{fmtMoney(h.avg_cost)}</td>
                    <td className="text-right px-3 py-3">{fmtMoney(h.current_price)}</td>
                    <td className={`text-right px-5 py-3 ${h.unrealized_pl >= 0 ? "profit" : "loss"}`}>
                      {fmtMoney(h.unrealized_pl, { showSign: true })}<br />
                      <span className="text-[10px]">{fmtPct(h.unrealized_pl_pct)}</span>
                    </td>
                  </tr>
                ))}
                {(!portfolio?.holdings || portfolio.holdings.length === 0) && (
                  <tr><td colSpan={5} className="px-5 py-12 text-center mono text-xs text-zinc-600">
                    No positions yet. <Link to="/trade" className="text-white underline">Place your first trade →</Link>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="lg:col-span-5">
          <CardHeader
            title="Bot activity"
            action={
              <Link to="/bots" className="mono text-[10px] text-zinc-400 hover:text-white uppercase tracking-widest flex items-center gap-1">
                <Robot size={12} /> {bots.filter(b => b.enabled).length} active →
              </Link>
            }
          />
          <div className="divide-y divide-zinc-800 max-h-[360px] overflow-y-auto">
            {botLogs.map((log) => {
              const sig = log.signal;
              const color = sig === "buy" ? "profit" : sig === "sell" ? "loss" : "text-zinc-400";
              return (
                <div key={log.id} className="px-5 py-3 flex items-start gap-3">
                  <Circle size={8} weight="fill" className={`mt-1.5 ${sig === "buy" ? "text-[#00C805]" : sig === "sell" ? "text-[#FF3B30]" : "text-zinc-600"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm flex items-center gap-2">
                      <span className="mono font-semibold">{log.symbol}</span>
                      <span className={`mono text-[10px] uppercase tracking-widest ${color}`}>{sig}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{log.message}</div>
                    <div className="mono text-[10px] text-zinc-600 mt-1">
                      {new Date(log.at).toLocaleTimeString()} · {log.strategy}
                    </div>
                  </div>
                </div>
              );
            })}
            {botLogs.length === 0 && (
              <div className="px-5 py-12 text-center mono text-xs text-zinc-600">
                No bot activity yet.<br />
                <Link to="/bots" className="text-white underline mt-2 inline-block">Configure a bot →</Link>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
